
import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, TrendingUp } from "lucide-react";

interface UserGrowthChartProps {
  isLoading: boolean;
  data: { month: string; users: number }[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-md shadow-md p-3">
        <p className="font-medium">{label}</p>
        <div className="space-y-1 mt-2">
          <p className="text-sm flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#8884d8]" />
            <span>Total Users: </span> 
            <span className="font-medium">{payload[0].value}</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const UserGrowthChart: React.FC<UserGrowthChartProps> = ({ isLoading, data }) => {
  // Ensure all data is valid, filter out any problematic entries
  const validData = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(entry => entry && entry.month && typeof entry.users === 'number');
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          User Growth
        </CardTitle>
        <CardDescription>New user registrations over time</CardDescription>
      </CardHeader>
      <CardContent className="h-[400px]">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : validData.length === 0 ? (
          <div className="flex h-full items-center justify-center flex-col">
            <TrendingUp className="h-12 w-12 text-muted-foreground mb-2" />
            <p className="font-medium text-muted-foreground">No user data available</p>
            <p className="text-xs text-muted-foreground">User registration data will appear here once users sign up</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={validData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="month" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
                width={30}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="users"
                name="Total Users"
                stroke="#8884d8"
                strokeWidth={2}
                activeDot={{ r: 8 }}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default UserGrowthChart;
